import { IAdmin } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, Select, FormInstance } from "antd";
import styled from "styled-components";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { FormAction } from "@components";
import { PATH_ADMINS, suggestedAdminPassword, userRoles } from "@configs";
import { setLoading } from "@redux";
import { getApiErrorMessage, useCreateAdmin, useNotify, useUpdateAdmin } from "@utils";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";

interface IProps {
  admin?: IAdmin;
}

const initValue: Partial<IAdmin> = {
  name: "",
  email: "",
};

export const AdminForm = memo(({ admin }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupAdmin, YupUpdateAdmin } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const clientSchema = Yup.object().shape(admin ? YupUpdateAdmin : YupAdmin);
  const [data, setData] = useState<Partial<IAdmin>>(initValue);
  const [clientForm] = Form.useForm();
  const password: string = Form.useWatch("password", clientForm);
  const confirmPassword: string = Form.useWatch("confirmPassword", clientForm);

  const isSubmitting = createAdmin.isPending || updateAdmin.isPending;

  const notSameValue = () => password !== confirmPassword;

  useEffect(() => {
    dispatch(setLoading(isSubmitting));
  }, [dispatch, isSubmitting]);

  useEffect(() => {
    if (admin) {
      setData({ ...data, ...admin });
      formRef.current?.setFieldsValue(admin);
    }
  }, [admin]);

  const onFinish = async (values: IAdmin & { password?: string; confirmPassword?: string }) => {
    if (values.password && notSameValue()) {
      error("passwords must match");
      return;
    }
    const { confirmPassword: _, ...payload } = values;

    try {
      if (admin) {
        await updateAdmin.mutateAsync({
          payload: { ...data, ...payload } as IAdmin,
          id: data?.userId || "",
        });
      } else {
        await createAdmin.mutateAsync({
          ...data,
          ...payload,
          status: true,
        } as IAdmin);
      }
      success(t("message.create.success"));
      history.push(PATH_ADMINS);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await clientSchema.validateSyncAt(field, { [field]: value });
    },
  };
  return (
    <Form
      form={clientForm}
      name="basic"
      ref={formRef}
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.user_name")} name="name" rules={[yupSync]}>
            <Input data-testid="admin-name" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.email")} name="email" rules={[yupSync]}>
            <Input type="email" data-testid="admin-email" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.user_role")} name="userRole" rules={[yupSync]}>
            <Select placeholder={t("page.selected_user_role")} data-testid="admin-role">
              {userRoles.map((item) => (
                <Select.Option key={item} value={item}>
                  {t(`page.${item}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {!admin && (
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item
              label={t("page.password")}
              name="password"
              rules={[yupSync]}
              extra={t("page.password_format_hint", { example: suggestedAdminPassword })}
            >
              <Input.Password placeholder={suggestedAdminPassword} data-testid="admin-password" />
            </Form.Item>
          </Col>
        )}
      </Row>
      {!admin && (
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.confirm_password")} name="confirmPassword" rules={[yupSync]}>
              <Input.Password data-testid="admin-confirm-password" />
            </Form.Item>
            <StyledP>{notSameValue() ? "passwords must match" : ""}</StyledP>
          </Col>
        </Row>
      )}
      <FormAction />
    </Form>
  );
});

const StyledP = styled.p`
  color: #ff4d4f;
  margin-top: -16px;
`;
